import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';

interface UseRememberedSelectionOptions<T> {
  key: string; // domain key, e.g. 'client'
  items?: T[]; // array of items to select from
  getId: (item: T) => string | undefined;
  basePath: string; // e.g. '/clients'
  routeParamName?: string; // defaults to 'id'
}

interface UseRememberedSelectionReturn<T> {
  selectedId?: string;
  selectedItem?: T;
  isReady: boolean;
  onSelect: (itemOrId: T | string) => void;
  clearStored: () => void;
}

export function useRememberedSelection<T>(
  options: UseRememberedSelectionOptions<T>,
): UseRememberedSelectionReturn<T> {
  const { key, items, getId, basePath, routeParamName = 'id' } = options;

  const history = useHistory();
  const params = useParams<Record<string, string | undefined>>();
  const paramId = params[routeParamName];

  const storageKey = useMemo(() => {
    const base = key.endsWith('s') ? key : `${key}s`;
    return `${base}:lastViewedId`;
  }, [key]);

  const readStoredId = useCallback(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw || undefined;
    } catch {
      return undefined;
    }
  }, [storageKey]);

  const setStoredId = useCallback(
    (id: string) => {
      window.localStorage.setItem(storageKey, id);
    },
    [storageKey],
  );

  const clear = useCallback(() => {
    window.localStorage.removeItem(storageKey);
    setSelectedId(undefined);
    initializationRef.current = false;
  }, [storageKey]);

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const initializationRef = useRef(false);

  const stableGetId = useCallback(getId, [getId]);

  useEffect(() => {
    if (paramId) {
      setSelectedId(paramId);
      setStoredId(paramId);
      initializationRef.current = true;
      return;
    }

    // Only run initialization logic once
    if (!initializationRef.current && items && items.length > 0) {
      const stored = readStoredId();
      const exists = stored ? items.some((i) => stableGetId(i) === stored) : false;
      const firstId = stableGetId(items[0]);
      const preferredId = exists ? (stored as string) : firstId;

      if (preferredId) {
        setSelectedId(preferredId);
        setStoredId(preferredId);
        history.replace(`${basePath}/${preferredId}`);
        initializationRef.current = true;
      } else {
        setSelectedId(undefined);
        initializationRef.current = true;
      }
    }
  }, [paramId, items, stableGetId, history, basePath, readStoredId, setStoredId]);

  const onSelect = useCallback(
    (itemOrId: T | string) => {
      const id = typeof itemOrId === 'string' ? itemOrId : stableGetId(itemOrId);
      if (!id) return;
      if (id === selectedId) return;
      setSelectedId(id);
      setStoredId(id);
      history.push(`${basePath}/${id}`);
    },
    [stableGetId, selectedId, setStoredId, history, basePath],
  );

  const selectedItem = useMemo(() => {
    if (!items || !selectedId) return undefined;
    return items.find((i) => stableGetId(i) === selectedId);
  }, [items, selectedId, stableGetId]);

  const isReady = Boolean(selectedId) || (items ? items.length === 0 : false);

  return {
    selectedId,
    selectedItem,
    isReady,
    onSelect,
    clearStored: clear,
  };
}
